import { isSystemDataSequenceId } from "building-types/dataSequences/isSystemDataSequenceId";
import type { Doc } from "building-types/doc";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { DocEntityHandle, IndeterminateHandle } from "building-types/handles/docEntityHandleTypes";
import type { Formula } from "building-types/options/formula";
import type {
  Parameter,
  ParameterRef,
  ParameterValue,
  ParameterValueHandle,
  PrimitiveValue,
} from "building-types/options/parameter";
import { entityBasedParameterTypes, ParameterType } from "building-types/options/parameter";
import type { Evaluated, ViewportViewType } from "building-types/views/planSets";
import type { Lazy } from "common/data/lazy";
import { tableMerge, type RWTable, type Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { isNullish } from "common/utils/isNullish";
import matchAll from "common/utils/matchAll";
import objectFilter from "common/utils/objectFilter";
import objectMap from "common/utils/objectMap";
import { camelCaseToTitleCase, snakeCaseToTitleCase } from "common/utils/strings";
import { docGetEntityHandleById } from "./entity/docGetEntityHandleById";
import { evalFormula } from "./formula/formulaEval";
import { isFormula } from "./formula/formulaParsing";
import { formatInchesFraction } from "./measurementUtils";
import { optionChoiceBySetAndChoiceId } from "./options/optionChoicesTools";
import { parameterFormatName } from "./parameterCoreTools";
import { formatViewScaleFactor } from "./viewScaleFormat";

/**
 * Parameter token regex to match "{{ paramId }}" patterns
 */
export const parameterTokenRegex = /\{\{[ ]*([A-Za-z_0-9]+)[ ]*\}\}/g;

/**
 * Checks if a string contains parameter tokens without extracting them
 */
export function stringContainsParameterTokens(text: string): boolean {
  parameterTokenRegex.lastIndex = 0;
  return parameterTokenRegex.test(text);
}

export function isParameterRef(v: any): v is ParameterRef {
  return (v as ParameterRef)?.type === "PARAMETER_REF";
}

function isParameterValueHandle(v: any): v is ParameterValueHandle {
  if (typeof v !== "object" || v === null || !v.type) {
    return false;
  }

  const type = v.type as ParameterValueHandle["type"];
  switch (type) {
    case "PRIMITIVE":
    case "PRESET":
    case "OPTION":
    case "LEVEL":
    case "UNIT":
    case "DATA_SEQUENCE":
    case "VIEW_TEMPLATE":
    case "CONTENT_TEMPLATE":
    case "ELEVATION_VIEW":
    case "SECTION_VIEW":
      return true;
    default:
      return false;
  }
}

export const parameterTypes = [
  "LOCAL_PARAMETER",
  "GLOBAL_PARAMETER",
  "SYSTEM_PARAMETER",
] as const satisfies Parameter["entityType"][];

export function parametersOfType(vars: Table<Parameter>, parameterType: ParameterType): Table<Parameter> {
  return objectFilter(vars, (v) => v.type === parameterType);
}

export function parametersWithParentAndType(
  vars: Table<Parameter>,
  parentId: string,
  parameterType: ParameterType,
): Table<Parameter> {
  return objectFilter(vars, (v) => v.parentId === parentId && v.type === parameterType);
}

const EMPTY_PARAM_VALUES: Table<ParameterValue | ParameterRef> = {};

const requiresParameterEvalCache = new WeakMap<object, boolean>();

function requiresParameterEval(v: unknown): boolean {
  if (typeof v !== "object" || v === null) {
    return false;
  }

  const cached = requiresParameterEvalCache.get(v);
  if (cached !== undefined) {
    return cached;
  }

  const res = requiresParameterEvalInner(v);
  requiresParameterEvalCache.set(v, res);
  return res;
}

function requiresParameterEvalInner(v: object): boolean {
  if (ArrayBuffer.isView(v) || v instanceof RegExp || v instanceof Date) {
    return false;
  }

  if (v instanceof Array) {
    for (let i = 0, length = v.length; i < length; i++) {
      if (requiresParameterEval(v[i])) {
        return true;
      }
    }
    return false;
  }

  if (isFormula(v) || isParameterRef(v) || isParameterValueHandle(v)) {
    return true;
  }

  for (const key in v) {
    if (!Object.hasOwn(v, key)) {
      continue;
    }

    if (requiresParameterEval(v[key])) {
      return true;
    }
  }

  return false;
}

function copyArrayOnFirstMutation(result: unknown[] | undefined, source: readonly unknown[]): unknown[] {
  if (result !== undefined) {
    return result;
  }
  return source.slice();
}

function copyRecordOnFirstMutation(
  result: Record<string, unknown> | undefined,
  source: Record<string, unknown>,
): Record<string, unknown> {
  if (result !== undefined) {
    return result;
  }
  return { ...source };
}

/**
 * Reads a value out of a parameter scope for entity and formula evaluation.
 *
 * A scope entry can itself be an unresolved {@link ParameterRef}: a binding that pointed at a
 * parameter carrying no value (see {@link evaluateParameterBindings}). Entities and formulas have
 * no way to represent a reference, so it reads as unset here. Annotations are the exception — they
 * keep the ref so they can render a `{{ referencedName }}` placeholder.
 */
function lookupParameterValue(
  paramValues: Table<ParameterValue | ParameterRef>,
  id: string,
): ParameterValue | undefined {
  const value = paramValues[id];
  return isParameterRef(value) ? undefined : value;
}

function evalFormulaWithParamValues(formula: Formula, paramValues: Table<ParameterValue | ParameterRef>): unknown {
  return evalFormula(formula, "", (v) => lookupParameterValue(paramValues, v));
}

function evaluateParameterLeaf(value: object, paramValues: Table<ParameterValue | ParameterRef>): unknown {
  if (isFormula(value)) {
    return evalFormulaWithParamValues(value, paramValues);
  }

  if (isParameterRef(value)) {
    return lookupParameterValue(paramValues, value.id);
  }

  if (isParameterValueHandle(value)) {
    return value.value;
  }

  return evaluateFormulasParameterRefsInObjectInner(value, paramValues);
}

function evaluateFormulasParameterRefsInObjectInner(
  v: object,
  paramValues: Table<ParameterValue | ParameterRef>,
): unknown {
  if (v instanceof Array) {
    const length = v.length;
    let result: unknown[] | undefined;
    for (let i = 0; i < length; i++) {
      const child = v[i];
      const evaluated = evaluateFormulasParameterRefsInObjectChild(child, paramValues);
      if (evaluated !== child) {
        result = copyArrayOnFirstMutation(result, v);
        result[i] = evaluated;
      }
    }
    return result ?? v;
  }

  let result: Record<string, unknown> | undefined;
  const record = v as Record<string, unknown>;

  for (const key in record) {
    if (!Object.hasOwn(record, key)) {
      continue;
    }

    const child = record[key];
    const evaluated = evaluateFormulasParameterRefsInObjectChild(child, paramValues);
    if (evaluated !== child) {
      result = copyRecordOnFirstMutation(result, record);
      result[key] = evaluated;
    }
  }

  return result ?? v;
}

function evaluateFormulasParameterRefsInObjectChild(
  child: unknown,
  paramValues: Table<ParameterValue | ParameterRef>,
): unknown {
  if (!requiresParameterEval(child)) {
    return child;
  }

  if (typeof child !== "object" || child === null) {
    return child;
  }

  return evaluateParameterLeaf(child, paramValues);
}

/**
 * Recursively evaluates ParameterRefs, Formulas, and ParameterValueHandles in an object. This is used to evaluate parameter references in entities.
 * The resultant value depends on the type, but it is replaced with the value stored in the parameter slot.
 */
export function evaluateFormulasParameterRefsInObject<T>(
  obj: T,
  paramValues: Table<ParameterValue | ParameterRef> = EMPTY_PARAM_VALUES,
): Evaluated<T> {
  if (!requiresParameterEval(obj)) {
    return obj as Evaluated<T>;
  }

  if (typeof obj !== "object" || obj === null) {
    return obj as Evaluated<T>;
  }

  return evaluateParameterLeaf(obj, paramValues) as Evaluated<T>;
}

/**
 * Evaluates a table of parameter bindings — the `parameterValues` an instance passes to the
 * template it instantiates — against the scope the instance lives in.
 *
 * This differs from {@link evaluateFormulasParameterRefsInObject} in one way: a binding that is a
 * {@link ParameterRef} whose target has no value is preserved as a ref instead of collapsing to
 * `undefined`. Both the binding and the collapsed form mean "no value", but only the ref records
 * *which* parameter was referenced, and it keeps the binding distinguishable from "never bound".
 * Annotations rely on both facts: they render the referenced parameter's `{{ name }}` placeholder
 * rather than falling back to the bound parameter's own default value, which the user replaced
 * when they pointed it at another parameter.
 */
export function evaluateParameterBindings(
  bindings: Table<ParameterValueHandle | ParameterValue | ParameterRef>,
  paramValues: Table<ParameterValue | ParameterRef>,
): Table<ParameterValue | ParameterRef> {
  if (!requiresParameterEval(bindings)) {
    return bindings as Table<ParameterValue | ParameterRef>;
  }

  const result: RWTable<ParameterValue | ParameterRef> = {};

  for (const [id, binding] of Object.entries(bindings)) {
    if (!isParameterRef(binding)) {
      result[id] = evaluateFormulasParameterRefsInObject(binding, paramValues) as ParameterValue;
      continue;
    }

    // Propagate the target's own unresolved ref when the reference is chained, so the placeholder
    // names the parameter at the end of the chain rather than an intermediate one.
    const resolved = paramValues[binding.id];
    result[id] = resolved === undefined ? binding : resolved;
  }

  return result;
}

/**
 * Evaluates a doc entity's disabled field, which may be a boolean or parameter formula.
 */
export function evaluateDisabledField(
  disabled: boolean | Formula | undefined,
  paramValues: Table<ParameterValue | ParameterRef>,
): boolean | undefined {
  if (disabled === undefined) {
    return undefined;
  }

  if (typeof disabled === "boolean") {
    return disabled;
  }

  const result = evalFormulaWithParamValues(disabled, paramValues);
  return Boolean(result);
}

export function listParameterRefsInObject(obj: any, list: ParameterRef[] = []): ParameterRef[] {
  if (typeof obj !== "object" || obj === null) {
    return list;
  }

  if (ArrayBuffer.isView(obj)) {
    return list;
  }

  if (Array.isArray(obj)) {
    obj.forEach((x) => listParameterRefsInObject(x, list));
    return list;
  }

  for (const key in obj) {
    const value = obj[key] as object;

    if (isParameterRef(value)) {
      list.push(value);
    }
  }

  return list;
}

/**
 * This is used by annotation UI components in Studio. The user never directly sees the ids in annotation but can insert
 * parameter references like "{{ parameterName }}".
 */
export function replaceParameterNamesWithIds(text: string, variables: Table<Parameter>): string {
  const replacements: [number, string, string | undefined][] = [];

  const vars = Object.values(variables);

  for (const match of matchAll(text, parameterTokenRegex)) {
    const matched = match[0];
    const variableName = match[1];
    const index = match.index;
    const param = vars.find((x) => x.name === variableName);
    if (param) {
      replacements.push([index, matched, param.id]);
    }
  }

  for (let i = replacements.length - 1; i >= 0; i--) {
    const [index, matched, paramId] = replacements[i];
    const token = parameterTokenNew(paramId);

    text = text.slice(0, index) + token + text.slice(index + matched.length);
  }

  return text;
}
/**
 * This is used by annotation UI components in Studio. The user never directly sees the ids in annotation but can insert
 * parameter references like "{{ parameterName }}".
 */

export function replaceParameterIdsWithNames(text: string, variables: Table<Parameter>): string {
  const replacements: [number, string, string | undefined][] = [];

  const vars = Object.values(variables);

  for (const match of matchAll(text, parameterTokenRegex)) {
    const matched = match[0];
    const variableId = match[1];
    const index = match.index;
    const param = vars.find((x) => x.id === variableId);
    if (param) {
      replacements.push([index, matched, param.name]);
    }
  }

  for (let i = replacements.length - 1; i >= 0; i--) {
    const [index, matched, variableName] = replacements[i];
    text = text.slice(0, index) + parameterTokenNew(variableName) + text.slice(index + matched.length);
  }

  return text;
}

const parameterIdRegexCache = new Map<string, RegExp>();

export function parameterIdRegex(paramId: string): RegExp {
  if (parameterIdRegexCache.has(paramId)) {
    return parameterIdRegexCache.get(paramId) as RegExp;
  }

  const regex = new RegExp(`\{\{[ ]*${paramId}[ ]*\}\}`, "g");
  parameterIdRegexCache.set(paramId, regex);
  return regex;
}

export function parameterTokenNew(paramId: string | undefined) {
  return `{{ ${paramId} }}`;
}

export function replaceParamIdsInPlanSetEntity<T extends DocEntityBase>(
  entity: T,
  parameters: Table<Parameter> = {},
  paramIdMapping: { [paramId: string]: string /* new param id */ } = {},
): T {
  const updatedEntity = { ...entity };

  for (const key in entity) {
    const value = entity[key];
    // Check if the value is coming from a parameter (post evaluation)
    const param = parameters[value as string];

    if (!param) {
      continue;
    }

    // Check if the parameter id has been replaced via some upstream operation
    const newParamId = paramIdMapping[param.id];

    // Replace the value with the new parameter id
    if (newParamId) {
      Object.assign(updatedEntity, { [key]: newParamId });
    }
  }

  return updatedEntity;
}

export function parameterWithNameCopy(parameter: Parameter, parameters: Table<Parameter>): Parameter {
  const siblingParameters = objectFilter(parameters, ({ parentId }) => parentId === parameter.parentId);
  // find parameters with the same name
  const sameNameParameters = objectFilter(siblingParameters, ({ name }) => name === parameter.name);
  const copyCount = Object.values(sameNameParameters).filter((x) => x.id !== parameter.id).length;

  return {
    ...parameter,
    name: `${parameterFormatName(parameter.name)}(copy${copyCount + 1})`,
  };
}

export function collectParameterIdsInString(string: string) {
  const paramIds = [...matchAll(string, parameterTokenRegex)].map((x) => x[1]);

  const foundParams: { [paramId: string]: string } = {};

  for (const id of paramIds) {
    foundParams[id] = id;
  }

  return foundParams;
}

export function docEntityCollectParameters<T extends DocEntityBase>(
  parameters: Table<Parameter>,
  entity: T,
): Table<Parameter> {
  const entityParameters: RWTable<Parameter> = {};

  for (const key in entity) {
    const value = entity[key];

    if (typeof value !== "string" || !stringContainsParameterTokens(value)) {
      continue;
    }

    const paramIds = collectParameterIdsInString(value);

    for (const paramId in paramIds) {
      const param = parameters[paramId];

      if (!param) {
        continue;
      }

      entityParameters[paramId] = param;
    }
  }

  return entityParameters;
}

export function evaluateParameterValueHandles(values: Table<ParameterValueHandle>): Table<ParameterValue> {
  const parameterValues = objectFilter(values, (v) => v !== undefined && v !== null);
  return objectMap(parameterValues, (v) => v.value);
}

export function evaluateParameterValueHandlesAndRefs(
  values: Table<ParameterValueHandle | ParameterRef>,
): Table<ParameterValue | ParameterRef> {
  const parameterValues = objectFilter(values, (value) => value !== undefined && value !== null);
  return objectMap(parameterValues, (v) => (isParameterRef(v) ? v : v.value));
}

export const viewportViewTypeToParameterType: Record<ViewportViewType, ParameterType> = {
  PLAN_VIEW: ParameterType.LEVEL,
  ELEVATION_VIEW: ParameterType.ELEVATION_VIEW,
  SECTION_VIEW: ParameterType.SECTION_VIEW,
  VIEW_3D: ParameterType.VIEW_3D,
  DRAWING: ParameterType.DRAWING_TEMPLATE,
};

export function docGetHandleFromParameterRef(doc: Doc, paramRef: ParameterRef): DocEntityHandle {
  const indeterminateHandle: IndeterminateHandle = {
    type: "INDETERMINATE_ENTITY_TYPE",
    entityTypes: ["LOCAL_PARAMETER", "GLOBAL_PARAMETER", "SYSTEM_PARAMETER"],
    id: paramRef.id,
  };

  const handle = docGetEntityHandleById(doc, paramRef.id, indeterminateHandle.entityTypes);
  return handle ?? indeterminateHandle;
}

export function parameterIsEntityBased(param?: Parameter): boolean {
  if (!param) {
    return false;
  }

  return entityBasedParameterTypes[param.type] !== undefined;
}

/**
 * Inserts a parameter token in a string containing parameter id tokens using a position from
 * the string containing parameter name tokens.
 *
 * @param text - The text containing parameter ***id*** tokens (e.g. "Level 1 Area: {{ xkoGB4mnv6t3qin6 }}, Level 2 Area: {{ ymJkG4mnv6t3qin6 }}")
 * @param position - The position in the text containing parameter ***name*** tokens (e.g. "Level 1 Area: {{ levelOneArea }}, Level 2 Area: {{ levelTwoArea }}") to insert the parameter token at
 * @param parameters - The parameters to use when replacing the parameter id tokens with parameter name tokens
 * @param newParamId - The id of the new parameter to insert
 */
export function insertParameterTokenAtPosition(
  text: string,
  position: number | undefined,
  parameters: Table<Parameter>,
  newParamId: string,
): string {
  const newParameterToken = parameterTokenNew(newParamId);

  if (position === undefined) {
    return `${text}${newParameterToken}`;
  }

  if (position === 0) {
    return `${newParameterToken}${text}`;
  }

  const textWithParameterNames = replaceParameterIdsWithNames(text, parameters);

  const newTextWithParameterNames = `${textWithParameterNames.slice(
    0,
    position,
  )}${newParameterToken}${textWithParameterNames.slice(position)}`;

  return replaceParameterNamesWithIds(newTextWithParameterNames, parameters);
}

export interface ParameterReplacementValueContext {
  doc: Pick<
    Doc,
    | "levels"
    | "compositeLevels"
    | "elevationViews"
    | "sectionViews"
    | "contentTemplates"
    | "presets"
    | "viewTemplates"
    | "dataSequences"
    | "optionSets"
    | "optionChoices"
    | "labels"
  >;
  values: Table<ParameterValue | ParameterRef>;
  params: Table<Parameter>;
}

export function getParameterReplacementValue(id: string, args: ParameterReplacementValueContext) {
  let replacement: string | undefined = "";
  if (!args.params[id]) {
    return replacement;
  }

  const value = args.values[id];

  if (isParameterRef(value)) {
    // The parameter is bound to a reference that resolved to nothing. Report it as unset so the
    // caller renders a placeholder; falling through would resurrect this parameter's own default
    // value, which the binding was meant to replace.
    return undefined;
  }

  const { type: parameterType, defaultValue } = args.params[id];

  switch (parameterType) {
    case ParameterType.TEXT: {
      replacement = (value as string) ?? (defaultValue as string);
      break;
    }
    case ParameterType.NUMBER: {
      replacement = (value as number)?.toString() ?? (defaultValue as number)?.toString();
      break;
    }
    case ParameterType.BOOLEAN: {
      replacement = (value as boolean)?.toString() ?? (defaultValue as boolean)?.toString();
      break;
    }
    case ParameterType.URL: {
      replacement = (value as string) ?? defaultValue;
      break;
    }
    case ParameterType.DRAWING_SCALE: {
      const val = (value ?? defaultValue) as number;
      if (typeof val === "number") {
        replacement = formatViewScaleFactor(val);
      }
      break;
    }
    case ParameterType.FOOT_INCH: {
      const val = (value ?? defaultValue) as number;
      if (typeof val === "number") {
        replacement = formatInchesFraction(val, 32);
      }
      break;
    }
    case ParameterType.LEVEL: {
      const contentSourceId = (value ?? defaultValue) as string;
      let contentSourceName: string | undefined;
      if (contentSourceId) {
        contentSourceName = args.doc.levels[contentSourceId]?.name ?? args.doc.compositeLevels[contentSourceId]?.name;
      }
      replacement = contentSourceName;
      break;
    }
    case ParameterType.UNIT: {
      const labelId = (value ?? defaultValue) as string;
      let unitName: string | undefined;
      if (labelId) {
        unitName = args.doc.labels[labelId]?.name;
      }
      replacement = unitName;
      break;
    }
    case ParameterType.ELEVATION_VIEW: {
      const elevationViewId = (value ?? defaultValue) as string;
      let elevationViewName: string | undefined;
      if (elevationViewId) {
        elevationViewName = args.doc.elevationViews[elevationViewId]?.name;
      }
      replacement = elevationViewName;
      break;
    }
    case ParameterType.SECTION_VIEW: {
      const sectionViewId = (value ?? defaultValue) as string;
      let sectionViewName: string | undefined;
      if (sectionViewId) {
        sectionViewName = args.doc.sectionViews[sectionViewId]?.name;
      }
      replacement = sectionViewName;
      break;
    }
    case ParameterType.VIEW_3D: {
      const view3DId = (value ?? defaultValue) as string;
      let view3DName: string | undefined;
      if (view3DId) {
        view3DName = camelCaseToTitleCase(view3DId);
      }
      replacement = view3DName;
      break;
    }
    case ParameterType.DRAWING_TEMPLATE: {
      const drawingTemplateId = (value ?? defaultValue) as string;
      let drawingTemplateName: string | undefined;
      if (drawingTemplateId) {
        drawingTemplateName = args.doc.contentTemplates[drawingTemplateId]?.name;
      }
      replacement = drawingTemplateName;
      break;
    }
    case ParameterType.PRESET: {
      const presetId = (value ?? defaultValue) as string;
      let presetName: string | undefined;
      if (presetId) {
        presetName = args.doc.presets[presetId]?.name;
      }
      replacement = presetName;
      break;
    }
    case ParameterType.VIEW_TEMPLATE: {
      const viewTemplateId = (value ?? defaultValue) as string;
      let viewTemplateName: string | undefined;
      if (viewTemplateId) {
        viewTemplateName = args.doc.viewTemplates[viewTemplateId]?.name;
      }
      replacement = viewTemplateName;
      break;
    }
    case ParameterType.DATA_SEQUENCE: {
      const dataSequenceId = (value ?? defaultValue) as string;
      let dataSequenceName: string | undefined;

      if (dataSequenceId && isSystemDataSequenceId(dataSequenceId)) {
        dataSequenceName = snakeCaseToTitleCase(dataSequenceId);
      } else if (dataSequenceId) {
        dataSequenceName = args.doc.dataSequences[dataSequenceId]?.name;
      }
      replacement = dataSequenceName;
      break;
    }
    case ParameterType.OPTION: {
      const optionChoiceId = (value ?? defaultValue) as string;
      let optionChoiceName: string | undefined;

      if (optionChoiceId) {
        const param = args.params[id];

        optionChoiceName = optionChoiceBySetAndChoiceId(args.doc.optionSets, param.optionSetId, optionChoiceId)?.name;
      }

      replacement = optionChoiceName;
      break;
    }
    default: {
      assertUnreachable(parameterType);
    }
  }
  return replacement;
}

export function parameterValuesMerge(
  a: Table<ParameterValue | ParameterRef>,
  b: Table<ParameterValue | ParameterRef | undefined | null>,
): void {
  tableMerge(a, b, (aVal, bVal) => (isNullish(bVal) || bVal === "" ? aVal : bVal));
}

/**
 * LazyPrimitiveAdapter is useful for temporary storage of primitive values that
 * should be lazily computed on value read in getParameterReplacementValue.
 * This adapter should not be used with values that need to be stored on the doc,
 * as serializability is not guaranteed.
 */
export class LazyPrimitiveAdapter implements PrimitiveValue {
  constructor(private lazyInstance: Lazy<ParameterValue>) {}

  public readonly type = "PRIMITIVE" as const;
  public get value() {
    return this.lazyInstance.value;
  }
  public toJSON() {
    return {
      type: "PRIMITIVE",
      value: this.value,
    };
  }
}
